import { Component, OnInit, ChangeDetectorRef, OnDestroy, Renderer2, ViewEncapsulation } from '@angular/core';
import { ToasterService } from 'src/app/_services/toaster.service';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import {
  TwoDimensionalAiAssessmentViewModel,
  TwoDimensionalAiAssessmentViewModelRead,
  SpeedUnit,
  DistanceUnit,
  WeightUOM,
  TwoDimensionalAiAssessmentProfileViewModel,
  MovementCapability,
  GenderPublic
} from 'src/app/_models/generatedModels';
import { AssessmentService } from 'src/app/_services/generatedServices';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbsService } from 'src/app/_services/breadcrumbs.service';
import { FormCanDeactivate } from 'src/app/_guards/canDeactivate.guard';
import { PublicVideoPopupExampleComponent } from './public-video-popup-example.component';
import { TermsAndConditionsDialogComponent } from '../../_components/terms-and-conditions-dialog/terms-and-conditions-dialog.component';
import { Enums } from 'src/app/_models/generatedEnums';
import { Subscription } from 'rxjs';

@Component({
  selector: 'public-bxl-add-2d-ai-assessment',
  templateUrl: 'public-add-2d-ai-assessment.component.html',
  styleUrls: ['public-add-2d-ai-assessment.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PublicAdd2DAiAssessmentComponent extends FormCanDeactivate implements OnInit, OnDestroy {
  initialized = false;
  finished = false;

  assessment: TwoDimensionalAiAssessmentViewModelRead | undefined;
  assessmentProfile: TwoDimensionalAiAssessmentProfileViewModel | undefined;

  readonly distanceUnit = DistanceUnit;
  readonly speedUnit = SpeedUnit;
  readonly weightUnit = WeightUOM;

  // Enumerations exposed to template
  yearsOfExperienceEnum = this.mapEnum(Enums.YearsOfExperienceEnum);
  runnerSlowdownFactorEnum = this.mapEnum(Enums.RunnerSlowdownFactorEnum);
  genderEnum = this.mapEnum(Enums.GenderPublicEnum);
  runnerMotivationEnum = this.mapEnum(Enums.RunnerMotivationEnum);
  targetRaceTypeEnum = this.mapEnum(Enums.TargetRaceTypeEnum);
  runningFormApproachEnum = this.mapEnum(Enums.RunningFormApproachEnum);
  injurySiteEnum = this.mapEnum(Enums.InjurySiteEnum);
  injurySideEnum = this.mapEnum(Enums.InjurySideEnum);
  injuryImpactEnum = this.mapEnum(Enums.InjuryImpactEnum);
  injuredAgo = this.mapEnum(Enums.InjuryAgoEnum);
  injuryImpactPastEnum = this.mapEnum(Enums.InjuryImpactPastEnum);

  public formGroup!: FormGroup;
  public formGroupProfile!: FormGroup;

  submitComplete: Promise<void> | undefined;
  submitProfileComplete: Promise<void> | undefined;

  // Wizard / stepper
  suppressCompletionStep = false;

  readonly steps: string[] = [
    'Submit',
    'Video uploading',
    'Level Up',
    'Runner Profile',
    'Injury',
    'Injury history',
    'RRA init',
    'RRA 1',
    'RRA 2',
    'RRA 3',
    'RRA 4',
    'RRA 5',
    'RRA 6',
    'Summary'
  ];

  readonly rraControls = [
    'hipMobilityLeft',
    'hipMobilityLeftPain',
    'hipMobilityRight',
    'hipMobilityRightPain',

    'lowerBodyStabilityCountsLeft',
    'lowerBodyStabilityLeftPain',
    'lowerBodyStabilityCountsRight',
    'lowerBodyStabilityRightPain',

    'ankleMobilityLeft',
    'ankleMobilityLeftPain',
    'ankleMobilityRight',
    'ankleMobilityRightPain',

    'calfStrengthRaisesLeft',
    'calfStrengthLeftPain',
    'calfStrengthRaisesRight',
    'calfStrengthRightPain',

    'hipStrengthLeft',
    'hipStrengthLeftPain',
    'hipStrengthRight',
    'hipStrengthRightPain',

    'coreSecondsStrength',
    'coreStrengthPain'
  ];


  hasRunnerProfileStarted = false;
  hasRrAssessmentStarted = false;
  currentStep = 0;
  showSideVideoInstruction = true;
  showBackVideoInstruction = true;
  isLoading = false;

  // Visited flags; first always true
  visitedSteps: boolean[] = this.steps.map((_, i) => i === 0);

  // Mapping of page index to form controls for validation
  readonly pageControlMap: { [pageIndex: number]: string[] } = {
    0: ['firstName', 'lastName', 'publicUserEmail', 'runnerWeight', 'runnerHeight'],
    1: ['videos', 'acceptTerm', 'allowUse'],
    2: [],
    3: ['runnerAge', 'yearsOfExperience', 'daysPerWeekRunning', 'gender', 'slowdownFactor', 'motivation', 'targetRace', 'runningFormApproach'],
    4: ['hasCurrentInjury', 'currentInjuryImpact', 'currentInjurySite', 'currentInjurySide'],
    5: ['hasPastInjury', 'pastInjuryImpact', 'pastInjurySite', 'pastInjurySide', 'injuredAgo'],
    6: [],
    7: ['hipMobilityLeft', 'hipMobilityLeftPain', 'hipMobilityRight', 'hipMobilityRightPain'],
    8: ['lowerBodyStabilityCountsLeft', 'lowerBodyStabilityLeftPain', 'lowerBodyStabilityCountsRight', 'lowerBodyStabilityRightPain'],
    9: ['ankleMobilityLeft', 'ankleMobilityLeftPain', 'ankleMobilityRight', 'ankleMobilityRightPain'],
    10: ['calfStrengthRaisesLeft', 'calfStrengthLeftPain', 'calfStrengthRaisesRight', 'calfStrengthRightPain'],
    11: ['hipStrengthLeft', 'hipStrengthLeftPain', 'hipStrengthRight', 'hipStrengthRightPain'],
    12: ['coreSecondsStrength', 'coreStrengthPain'],
    13: [],
    14: []
  };

  pageForms: { [pageIndex: number]: FormGroup | null } = {};

  private injurySubscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToasterService,
    private modalService: NgbModal,
    private assessmentService: AssessmentService,
    private fb: FormBuilder,
    private breadcrumbs: BreadcrumbsService,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    super();
  }

  ngOnInit(): void
  {
    this.addFontLinks();
    this.buildAssessmentForm();
    this.buildProfileForm();
    this.mapPageForms();
    this.listenToHasInjury();
    this.initialized = true;
  }

  ngOnDestroy(): void {
    this.injurySubscriptions.forEach(s => s.unsubscribe());
  }

  // ----- Form construction -----
  private buildAssessmentForm(): void {
    const videos = this.fb.array(
      [this.createVideoGroup(), this.createVideoGroup()],
      [this.twoVideosValidator()]
    );

    this.formGroup = this.fb.group({
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      publicUserEmail: [this.assessment?.publicUserEmail ?? null, [Validators.required, Validators.email]],
      assessmentNotes: [this.assessment?.assessmentNotes ?? null],
      videos: videos,
      runnerWeight: [null, [Validators.required, Validators.min(30)]],
      weightUnit: [1],
      shoes: [null],
      runnerHeight: [null, [Validators.required, Validators.min(40)]],
      heightUnit: [2],
      allowUse: [false, [Validators.requiredTrue]],
      acceptTerm: [false, [Validators.requiredTrue]],
      isInjured: [false]
    });
  }

  private buildProfileForm(): void {
    this.formGroupProfile = this.fb.group({
      yearsOfExperience: [1, [Validators.required]],
      daysPerWeekRunning: [1, [Validators.required]],
      runnerAge: [null, [Validators.required]],
      gender: [null, [Validators.required]],
      slowdownFactor: [null, [Validators.required]],
      motivation: [null, [Validators.required]],
      targetRace: [null, [Validators.required]],
      runningFormApproach: [null, [Validators.required]],

      hasCurrentInjury: [false],
      currentInjuryImpact: [{ value: null, disabled: true }],
      currentInjurySite: [{ value: null, disabled: true }],
      currentInjurySide: [{ value: null, disabled: true }],

      hasPastInjury: [false],
      pastInjuryImpact: [{ value: null, disabled: true }],
      pastInjurySite: [{ value: null, disabled: true }],
      pastInjurySide: [{ value: null, disabled: true }],
      injuredAgo: [{ value: null, disabled: true }],

      hipMobilityLeft: [null, [Validators.required]],
      hipMobilityLeftPain: [null, [Validators.required]],
      hipMobilityRight: [null, [Validators.required]],
      hipMobilityRightPain: [null, [Validators.required]],

      lowerBodyStabilityCountsLeft: [null, [Validators.required, Validators.min(0)]],
      lowerBodyStabilityLeftPain: [null, [Validators.required]],
      lowerBodyStabilityCountsRight: [null, [Validators.required, Validators.min(0)]],
      lowerBodyStabilityRightPain: [null, [Validators.required]],

      ankleMobilityLeft: [null, [Validators.required]],
      ankleMobilityLeftPain: [null, [Validators.required]],
      ankleMobilityRight: [null, [Validators.required]],
      ankleMobilityRightPain: [null, [Validators.required]],

      calfStrengthRaisesLeft: [null, [Validators.required, Validators.min(0)]],
      calfStrengthLeftPain: [null, [Validators.required]],
      calfStrengthRaisesRight: [null, [Validators.required, Validators.min(0)]],
      calfStrengthRightPain: [null, [Validators.required]],

      hipStrengthLeft: [null, [Validators.required]],
      hipStrengthLeftPain: [null, [Validators.required]],
      hipStrengthRight: [null, [Validators.required]],
      hipStrengthRightPain: [null, [Validators.required]],

      coreSecondsStrength: [null, [Validators.required, Validators.min(0)]],
      coreStrengthPain: [null, [Validators.required]],
    });
  }

  private mapPageForms(): void {
    // Map each step to appropriate form (kept for template compatibility)
    this.pageForms = this.steps.reduce<{ [k: number]: FormGroup | null }>((acc, _, idx) => {
      acc[idx] = idx <= 1 ? this.formGroup : this.formGroupProfile;
      return acc;
    }, {});
  }

  // ----- Enum helper -----
  private mapEnum(map: Map<number, string>)
  {
    return Array.from(map.entries())
      .slice(1) // remove index 0 if it's unwanted
      .map(([value, label]) => ({
        value,
        label
      }));
  }

  // ----- Video form -----
  private createVideoGroup(): FormGroup {
    return this.fb.group({
      id: [0],
      videoId: [null],
      description: [null]
    });
  }

  listenToHasInjury()
  {
    // Current injury logic
    this.formGroupProfile.get('hasCurrentInjury')?.valueChanges.subscribe((value: boolean) =>
    {
      const currentControls = [
        'currentInjurySite',
        'currentInjurySide',
        'currentInjuryImpact'
      ];

      currentControls.forEach(ctrlName =>
      {
        const control = this.formGroupProfile.get(ctrlName);
        if (value) {
          control?.enable();
          control?.setValidators([Validators.required]);
        } else {
          control?.reset();
          control?.disable();
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
    });

    // Past injury logic
    this.formGroupProfile.get('hasPastInjury')?.valueChanges.subscribe((value: boolean) =>
    {
      const pastControls = [
        'pastInjurySite',
        'pastInjurySide',
        'pastInjuryImpact',
        'injuredAgo'
      ];

      pastControls.forEach(ctrlName =>
      {
        const control = this.formGroupProfile.get(ctrlName);
        if (value) {
          control?.enable();
          control?.setValidators([Validators.required]);
        } else {
          control?.reset();
          control?.disable();
          control?.clearValidators();
        }
        control?.updateValueAndValidity();
      });
    });
  }


  get videos(): FormArray {
    return this.formGroup.get('videos') as FormArray;
  }

  trackByIndex(index: number): number {
    return index;
  }

  onDecimalInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(/[^0-9.]/g, '');
  }

  // ----- Save flows -----
  onSaveAssessmentAndProfile(): void
  {

    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.toastr.error('Please press back and fill out all required fields', 'Error');
      return;
    }

    if (!this.formGroupProfile.valid) {
      this.formGroupProfile.markAllAsTouched();
      this.toastr.error('Please fill out all required fields', 'Error');
      return;
    }

    this.isLoading = true;


    this.hasRunnerProfileStarted = true;
    this.hasRrAssessmentStarted = true;
    this.suppressCompletionStep = true;

    this.onSave();

    this.submitComplete?.then(() => {
      this.onSaveProfile();
      return this.submitProfileComplete;
    }).then(() => {
      this.suppressCompletionStep = false;
      this.moveToCompletedStep();
      this.isLoading = false;
    }).catch(err =>
    {
      this.toastr.error('Error saving assessment or profile', err);
      this.suppressCompletionStep = false;
      this.isLoading = false;
    });
  }

  onEarlySaveAssessmentAndProfile(): void {
 
    this.rraControls.forEach(name =>
    {
      const ctl = this.formGroupProfile.get(name);
      ctl?.setErrors(null);
      //ctl?.markAsUntouched();
      //ctl?.markAsPristine();
    })

    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.toastr.error('Please press back and fill out all required fields', 'Error');
      return;
    }

    if (!this.formGroupProfile.valid) {
      this.formGroupProfile.markAllAsTouched();
      this.toastr.error('Please press back and fill out all required fields', 'Error');
      return;
    }

    this.toastr.confirmDialog('Are you sure you want to skip the Runner Movement Screen? This additional information is used to further personalize your report and plan.', 'Skip').subscribe(result => {
      if (result) {

        this.isLoading = true;

        this.hasRunnerProfileStarted = true;
        this.suppressCompletionStep = true;

        this.onSave();

        this.submitComplete?.then(() =>
        {
          this.onSaveProfile();
          return this.submitProfileComplete;
        }).then(() =>
        {
          this.suppressCompletionStep = false;
          this.moveToCompletedStep();
          this.isLoading = false;
        }).catch(err =>
        {
          this.toastr.error('Error saving assessment or profile', err);
          this.suppressCompletionStep = false;
          this.isLoading = false;
        });
      }
      else {
        this.rraControls.forEach(name =>
        {
          const ctl = this.formGroupProfile.get(name);
          ctl?.updateValueAndValidity();
        })
      }
    });
  }

  onSaveProfile(): void {
    if (!this.formGroupProfile.valid) {
      this.formGroupProfile.markAllAsTouched();
      this.toastr.error('Please press back and fill out all required fields', 'Error');
      return;
    }

    this.formGroupProfile.markAsPristine();
    this.submitProfileComplete = new Promise<void>((resolve) => {
      const model = this.mapFormToAssessmentProfileModel();
      this.saveProfile(model, resolve);
    });
  }

  onSave(): void {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.toastr.error('Please press back and fill out all required fields', 'Error');
      return;
    }

    this.formGroup.markAsPristine();
    this.submitComplete = new Promise<void>((resolve) => {
      const formData: TwoDimensionalAiAssessmentViewModel = { ...this.formGroup.value };
      formData.videos = formData.videos.filter((x: any) => x.videoId != null);
      this.saveAssessment(formData, resolve);
    });
  }

  onEarlySave(): void {
    if (!this.formGroup.valid) {
      this.formGroup.markAllAsTouched();
      this.toastr.error('Please press back and fill out all required fields', 'Error');
      return;
    }

    this.toastr.confirmDialog('Are you sure you want to skip building your running profile? This additional information is used to further personalize your report and plan.', 'Skip').subscribe(result => {
      if (result) {
        this.isLoading = true;

        this.onSave();

        this.submitComplete?.then(() =>
        {
          this.isLoading = false;
        }).catch(err =>
        {
          this.toastr.error('Error occured', err);
          this.isLoading = false;
        });
      }
    });
  }

  private saveAssessment(formData: TwoDimensionalAiAssessmentViewModel, done: () => void): void {
    this.assessmentService.createPublicTwoDimensionalAiAssessment(formData).subscribe({
      next: assessment => {
        this.assessment = assessment;
        if (!this.suppressCompletionStep) this.moveToCompletedStep();
        done();
      },
      error: () => done()
    });
  }

  private saveProfile(formData: TwoDimensionalAiAssessmentProfileViewModel, done: () => void): void {
    this.assessmentService.updatePublicTwoDimensionalAiAssessmentProfile(formData).subscribe({
      next: profile => {
        this.assessmentProfile = profile;
        if (!this.suppressCompletionStep) this.moveToCompletedStep();
        done();
      },
      error: () => done()
    });
  }

  // ----- Mapping -----
  private mapFormToAssessmentProfileModel(): TwoDimensionalAiAssessmentProfileViewModel {
    const f = this.formGroupProfile.value;

    const model: TwoDimensionalAiAssessmentProfileViewModel = {
      ...f,
      assessmentPublicId: this.assessment?.publicId,
      hasRunnerProfileStarted: this.hasRunnerProfileStarted,
      hasRrAssessmentStarted: this.hasRrAssessmentStarted,
      hipMobilityLeft: this.mapYesNoPain(f.hipMobilityLeft, f.hipMobilityLeftPain),
      hipMobilityRight: this.mapYesNoPain(f.hipMobilityRight, f.hipMobilityRightPain),
      lowerBodyStabilityLeft: this.mapLowerBodyStability(f.lowerBodyStabilityCountsLeft, f.lowerBodyStabilityLeftPain),
      lowerBodyStabilityRight: this.mapLowerBodyStability(f.lowerBodyStabilityCountsRight, f.lowerBodyStabilityRightPain),
      ankleMobilityLeft: this.mapYesNoPain(f.ankleMobilityLeft, f.ankleMobilityLeftPain),
      ankleMobilityRight: this.mapYesNoPain(f.ankleMobilityRight, f.ankleMobilityRightPain),
      calfStrengthLeft: this.mapCalfStrength(f.calfStrengthRaisesLeft, f.calfStrengthLeftPain, f.runnerAge),
      calfStrengthRight: this.mapCalfStrength(f.calfStrengthRaisesRight, f.calfStrengthRightPain, f.runnerAge),
      hipStrengthLeft: this.mapYesNoPain(f.hipStrengthLeft, f.hipStrengthLeftPain),
      hipStrengthRight: this.mapYesNoPain(f.hipStrengthRight, f.hipStrengthRightPain),
      coreStrength: this.mapCoreStrength(f.coreSecondsStrength, f.coreStrengthPain, f.gender)
    };

    return model;
  }

  private mapYesNoPain(value: boolean, pain: boolean): MovementCapability {
    if (pain) return MovementCapability.Pain;
    return value ? MovementCapability.Able : MovementCapability.Unable;
  }

  private mapLowerBodyStability(count: number, pain: boolean): MovementCapability {
    if (pain) return MovementCapability.Pain;
    const required = 7;
    return count >= required ? MovementCapability.Able : MovementCapability.Unable;
  }

  private mapCalfStrength(count: number, pain: boolean, runnerAge: number): MovementCapability
  {
    if (pain) return MovementCapability.Pain;
    const isYounger = runnerAge >= 18 && runnerAge <= 59;
    const threshold = isYounger ? 25 : 10;

    return count >= threshold
      ? MovementCapability.Able
      : MovementCapability.Unable;
  }

  private mapCoreStrength(seconds: number, pain: boolean, gender: GenderPublic): MovementCapability {
    if (pain) return MovementCapability.Pain;
    const required = gender === GenderPublic.Female ? 90 : 120;
    return seconds >= required ? MovementCapability.Able : MovementCapability.Unable;
  }

  // ----- Wizard navigation -----
  moveToCompletedStep(): void {
    this.finished = true;
    this.currentStep = this.steps.length - 1;
  }

  nextStep(): void {
    if (!this.isCurrentPageValid() || this.currentStep == 13) {
      this.touchCurrentPage();
      return;
    }
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      this.visitedSteps[this.currentStep] = true;
      this.cd.detectChanges();
      this.scrollToTop();
    }
  }

  previousStep(): void
  {
    if (this.currentStep > 0 && this.currentStep != 13) {
      this.currentStep--;
      this.cd.detectChanges();
      this.scrollToTop();
    }
  }

  goToStep(stepIndex: number): void
  {
    if (this.finished || this.currentStep == 13)
      return;
    if (!this.visitedSteps[stepIndex])
      return;

    if (!this.isCurrentPageValid()) {
      this.touchCurrentPage();
    }

    this.cd.detectChanges();
    this.currentStep = stepIndex;
    this.scrollToTop();
  }

  // ----- Page validation helpers -----
  isCurrentPageValid(): boolean {
    const controls = this.pageControlMap[this.currentStep];
    if (!controls || controls.length === 0)
      return true;

    return controls.every(name => {
      const ctrl = this.getControl(name);
      return !!ctrl && (!ctrl.enabled || ctrl.valid);
    });
  }

  touchCurrentPage(): void {
    const controls = this.pageControlMap[this.currentStep];
    if (!controls) return;
    controls.forEach(name => {
      const ctrl = this.getControl(name);
      if (ctrl) {
        ctrl.markAsTouched();
        ctrl.markAsDirty();
      }
    });
  }

  isPageInvalid(pageIndex: number): boolean {
    const controls = this.pageControlMap[pageIndex];
    if (!controls || controls.length === 0) return false;
    return controls.some(name => {
      const ctrl = this.getControl(name);
      return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
    });
  }

  getControl(controlName: string): AbstractControl | null {
    return this.formGroup.get(controlName) ?? this.formGroupProfile.get(controlName);
  }

  // ----- Validators -----
  private twoVideosValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const videos = control as FormArray;
      const valid = videos.controls.every(v => !!v.get('videoId')?.value);
      return valid ? null : { twoVideosRequired: true };
    };
  }

  onViewTerms(): void {
    this.modalService.open(TermsAndConditionsDialogComponent, { size: 'lg', windowClass: 'modal-xl-custom' });
  }

  private addFontLinks()
  {
    // 1. Preconnect to fonts.googleapis.com
    const link1 = document.createElement('link');
    link1.rel = 'preconnect';
    link1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(link1);

    // 2. Preconnect to fonts.gstatic.com (must include crossorigin)
    const link2 = document.createElement('link');
    link2.rel = 'preconnect';
    link2.href = 'https://fonts.gstatic.com';
    link2.crossOrigin = 'anonymous';
    document.head.appendChild(link2);

    // 3. Optional: load the actual font CSS
    const link3 = document.createElement('link');
    link3.rel = 'stylesheet';
    link3.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap';
    document.head.appendChild(link3);
  }

  toggleSideVideoInstruction()
  {
    this.showSideVideoInstruction = !this.showSideVideoInstruction;
  }


  toggleBackVideoInstruction()
  {
    this.showBackVideoInstruction = !this.showBackVideoInstruction;
  }

  onSelectYoe(value: any)
  {
    this.formGroupProfile.get('yearsOfExperience')?.setValue(value);
  }

  onSelectDayWeek(value: any)
  {
    this.formGroupProfile.get('daysPerWeekRunning')?.setValue(value);
  }

  openInstruction(link: string, exc: string) {
    const modalRef = this.modalService.open(PublicVideoPopupExampleComponent, { size: 'lg', ariaLabelledBy: 'modal-basic-title' });
    modalRef.componentInstance.analysisName = exc;
    modalRef.componentInstance.videoLink = link;
    modalRef.componentInstance.description = '';
    modalRef.componentInstance.encoded = true;

    modalRef.result.then(
      (result) => { },
      (reason) => { }
    );
  }


  goToCreateAssessment() {
    this.router.navigate(['/public-ai-assessment']).then(() =>
    {
      window.location.reload();
    });
  }

  scrollToTop()
  {
    let scrollers = [];
    document.querySelectorAll('*').forEach(el =>
    {
      const style = getComputedStyle(el);
      if (style.overflowY === 'scroll' || style.overflowY === 'auto') {
        scrollers.push(el);
      }
    });

    scrollers[0].scrollTo({ top: 0, behavior: 'smooth' });
  }
}
